// Mantém a Jornada de Configuração sincronizada com o backend enquanto o usuário
// a percorre. Re-consulta `GET /config-journey` (via carregarJornada forceUpdate)
// em 2 gatilhos:
//   - foco da aba (visibilitychange → visible): o usuário fez a ação numa
//     tela/aba e voltou; ao voltar, o status curado do backend chega.
//   - poll leve (POLL_INTERVAL_MS): pega conclusões assíncronas (integração que
//     terminou em background) sem o usuário fazer nada. Pausa enquanto a aba
//     está oculta — nada muda na tela e o gatilho de foco já refaz a consulta
//     ao voltar, então seguir batendo seria request desperdiçado por escritório.
// O mount/navegação disparam refresh nos próprios consumidores (home carrega no
// created; drawer chama no goNext). O status que o backend devolve é resolvido por
// SUJEITO, não por escritório (spec-005): etapa de escopo `escritorio` conta a
// marcação de qualquer pessoa do escritório; etapa de escopo `profissional`, só a
// de quem está logado — e o gestor é a entidade escritório, então enxerga as duas.
// É justamente por isso que este refresh existe: uma etapa pode virar `done` por
// ação de OUTRA pessoa, sem nada acontecer nesta aba.
//
// Uso: aplicar o mixin e chamar startJourneyRefresh()/stopJourneyRefresh() no
// ciclo de vida relevante (home: mounted/beforeDestroy; drawer: watch do estado
// ativo). O beforeDestroy do mixin garante a limpeza do listener e do interval.
import { apiCentralConfig } from '@modules/central-config/api/apiCentralConfig'

export const POLL_INTERVAL_MS = 60000
// Nº de falhas consecutivas do refresh de fundo (poll/foco) até sinalizar
// jornada "desatualizada" via journeyStale — evita alarme numa falha isolada.
export const STALE_AFTER_FAILURES = 3

export const journeyRefreshMixin = {
  data() {
    return {
      journeyPollId: null,
      journeyRefreshFailures: 0,
      // Refresh ligado pelo consumidor. Distinto de `journeyPollId`, que é só o
      // handle do interval e fica null enquanto a aba está oculta.
      journeyRefreshActive: false,
      // Superfície passiva liga o refresh sem poll — ver startJourneyRefresh.
      journeyPollHabilitado: true,
    }
  },
  computed: {
    // Refresh de fundo falhando persistentemente (rede fora, sessão expirada) —
    // sem isso, a jornada "congela" sem nenhum sinal pro usuário (silent:true
    // nunca mostra loading/erro). Reseta sozinho no próximo sucesso.
    journeyStale() {
      return this.journeyRefreshFailures >= STALE_AFTER_FAILURES
    },
  },
  beforeDestroy() {
    this.stopJourneyRefresh()
  },
  methods: {
    // Fire-and-forget: o dedup de request em voo do apiCentralConfig
    // (this.reqCarregar) impede chamadas concorrentes empilhadas.
    async refreshJourney() {
      // silent: refresh de fundo (poll/foco) NÃO mostra loading e só re-renderiza
      // se a jornada mudou de verdade — evita o "clarão" a cada poll.
      const r = await apiCentralConfig.carregarJornada({
        forceUpdate: true,
        silent: true,
      })
      this.journeyRefreshFailures =
        r && r.status === 200 ? 0 : this.journeyRefreshFailures + 1
    },
    isJourneyTabVisible() {
      return typeof document === 'undefined' || document.visibilityState === 'visible'
    },
    onJourneyVisible() {
      if (!this.journeyRefreshActive) return
      if (!this.isJourneyTabVisible()) {
        this.stopJourneyPoll()
        return
      }
      if (this.journeyPollHabilitado) this.startJourneyPoll()
      this.refreshJourney()
    },
    startJourneyPoll() {
      if (this.journeyPollId) return
      this.journeyPollId = setInterval(this.refreshJourney, POLL_INTERVAL_MS)
    },
    stopJourneyPoll() {
      if (!this.journeyPollId) return
      clearInterval(this.journeyPollId)
      this.journeyPollId = null
    },
    // ★ `comPoll: false` para superfície PASSIVA (2026-08-05, review do PR #1854).
    //
    // A barra embutida vive nas Visões Gerais de 3 módulos — telas de alto
    // tráfego onde o usuário não está executando a Jornada. Com o poll ligado,
    // qualquer pessoa parada ali gerava 1 `GET /config-journey` por minuto onde
    // antes não gerava nenhum, e o dado que ela mostra não muda sozinho: quem o
    // muda é o próprio usuário, marcando etapa em OUTRA aba — que é exatamente o
    // que o `visibilitychange` pega ao voltar.
    //
    // O poll continua ligado onde ele tem razão de existir: a home da Jornada e o
    // drawer, onde a conclusão pode vir de uma integração assíncrona enquanto a
    // tela está aberta.
    startJourneyRefresh({ comPoll = true } = {}) {
      if (this.journeyRefreshActive) return
      this.journeyRefreshActive = true
      this.journeyPollHabilitado = comPoll
      document.addEventListener('visibilitychange', this.onJourneyVisible)
      if (comPoll && this.isJourneyTabVisible()) this.startJourneyPoll()
    },
    stopJourneyRefresh() {
      this.journeyRefreshActive = false
      document.removeEventListener('visibilitychange', this.onJourneyVisible)
      this.stopJourneyPoll()
    },
  },
}
